import React, { ReactNode, useEffect, useState } from 'react';

import { Loader, Menu, Text } from '@mantine/core';
import {
  IconDownload,
  IconEdit,
  IconFile,
  IconPencil,
  IconPlus
} from '@tabler/icons-react';

import { useLazyGetTransmissionReportIdsQuery } from '@/store/slices/api/patient/patientListApiSlice';

import { PatientData } from '@/types/patientList';

import ReportCreateModal from '../../TransmissionReportPage/CreateReport';
import { usePatientActions } from '../hooks/usePatientActions';

import { BatchDownloadModal } from './BatchDownloadModal';

interface PatientOptionsMenuV2Props {
  patient: PatientData;
  opened: boolean;
  onOpenChange: (opened: boolean) => void;
  children: ReactNode;
}

export const PatientOptionsMenuV2: React.FC<PatientOptionsMenuV2Props> = ({
  patient,
  opened,
  onOpenChange,
  children
}) => {
  const [
    getTransmissionReportIds,
    { data: transmissionReportIdsData, isLoading: isLoadingReportIds }
  ] = useLazyGetTransmissionReportIdsQuery();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const {
    isDownloadingPrevious,
    isDownloadingNotDownloaded,
    isDownloadingAll,
    batchDownloadState,
    allReportsBatchState,
    setBatchDownloadState,
    setAllReportsBatchState,
    hasCurrentReport,
    hasPreviousReport,
    notDownloadedCount,
    onViewReport,
    onEditReport,
    onViewPreviousReport,
    onDownloadPreviousReport,
    onDownloadNotDownloadedReports,
    onDownloadAllReports,
    handleBatchDownload,
    handleAllReportsBatchDownload,
    handleClose,
    handleAllReportsClose
  } = usePatientActions(patient, () => onOpenChange(false));

  useEffect(() => {
    if (opened && !transmissionReportIdsData) {
      getTransmissionReportIds({ patientIds: [patient.id.toString()] });
    }
  }, [opened, patient.id, getTransmissionReportIds, transmissionReportIdsData]);

  const allReportIds =
    transmissionReportIdsData?.[patient.id.toString()]?.map((id: any) => id.toString()) || [];
  const allReportsCount = allReportIds.length;

  return (
    <>
      <Menu
        shadow="md"
        width={260}
        position="bottom-end"
        offset={5}
        opened={opened}
        onChange={onOpenChange}
        closeOnItemClick={false}>
        <Menu.Target>{children}</Menu.Target>

        <Menu.Dropdown style={{ borderRadius: 12, padding: '4px 0' }}>
          <Menu.Item leftSection={<IconPlus size={16} />} onClick={() => setIsCreateModalOpen(true)}>
            Create new report
          </Menu.Item>
          <Menu.Item
            leftSection={<IconPencil size={16} />}
            onClick={onViewReport}
            disabled={!hasCurrentReport}>
            View & sign report
          </Menu.Item>
          <Menu.Item
            leftSection={<IconEdit size={16} />}
            onClick={onEditReport}
            disabled={!hasCurrentReport}>
            Edit report
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFile size={16} />}
            onClick={onViewPreviousReport}
            disabled={!hasPreviousReport}>
            View previous report
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>
            <Text size="xs" c="dimmed">Download</Text>
          </Menu.Label>

          <Menu.Item
            leftSection={isDownloadingPrevious ? <Loader size={16} /> : <IconDownload size={16} />}
            onClick={onDownloadPreviousReport}
            disabled={!hasPreviousReport || isDownloadingPrevious}
            c={hasPreviousReport ? 'plum' : 'dimmed'}>
            Previous report
          </Menu.Item>
          <Menu.Item
            leftSection={isDownloadingNotDownloaded ? <Loader size={16} /> : <IconDownload size={16} />}
            onClick={onDownloadNotDownloadedReports}
            disabled={notDownloadedCount === 0 || isDownloadingNotDownloaded}
            c={notDownloadedCount > 0 ? 'plum' : 'dimmed'}>
            Not downloaded ({notDownloadedCount})
          </Menu.Item>
          <Menu.Item
            leftSection={isDownloadingAll ? <Loader size={16} /> : <IconDownload size={16} />}
            onClick={() => onDownloadAllReports(allReportIds)}
            disabled={isLoadingReportIds || allReportsCount === 0 || isDownloadingAll}
            c={allReportsCount > 0 ? 'plum' : 'dimmed'}>
            {isLoadingReportIds ? 'All reports (...)' : `All reports (${allReportsCount})`}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <BatchDownloadModal
        batchDownloadState={batchDownloadState}
        setBatchDownloadState={setBatchDownloadState}
        onDownload={handleBatchDownload}
        onClose={handleClose}
        notDownloadedCount={notDownloadedCount}
      />

      <BatchDownloadModal
        batchDownloadState={allReportsBatchState}
        setBatchDownloadState={setAllReportsBatchState}
        onDownload={handleAllReportsBatchDownload}
        onClose={handleAllReportsClose}
        notDownloadedCount={allReportsCount}
        title="All Reports Download"
        downloadingMessage={`Preparing ${allReportsCount} reports for download...`}
        successMessage={`${allReportsCount} reports are ready for download!`}
      />

      {isCreateModalOpen && (
        <ReportCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          patientId={patient.id}
        />
      )}
    </>
  );
};
